import { AuthedPage } from "pages/AuthedPage";
import { useRouter } from "next/router";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import {
  AnnotatedDeepLink,
  DeepLinkType,
  ExploreCard,
  DigestV2State,
} from "pages/api/deep-links/types";
import { useEffect, useMemo, useState } from "react";
import { theme, Dropdown, Button, Tooltip, Spin } from "antd";
import Link from "next/link";
import Icon, {
  AppstoreOutlined,
  FileTextOutlined,
  UnorderedListOutlined,
  EditOutlined,
  EllipsisOutlined,
  LoadingOutlined,
  StarFilled,
} from "@ant-design/icons";
import { Flex } from "src/components/Flex";
import {
  ensureState,
  serializeParams,
  unserializeParams,
} from "src/search/lib/params-state-reducer";
import { serializeContentView } from "pages/[orgId]/explore/lib/useContentView";
import { getCorrectedContentView } from "pages/[orgId]/boards/lib";
import { serializeDocument } from "pages/[orgId]/explore/lib/useExploreDocument";
import { serializeTabs } from "pages/[orgId]/explore/lib/useTabs";
import { useApplicationContext } from "src/hooks/useApplication";
import { ChatAssistant } from "src/search/components/ChatAssistant";
import { Condition } from "src/search/types";
import { toSection } from "pages/[orgId]/feed/Digest";
import { ToasterMessageType, useToasterContext } from "src/hooks/useToaster";
import { StarIcon } from "src/components/StarIcon";
import { NavigableDeepLink } from "src/components/NavigableDeepLink";
import { CardRenderer } from "../cards/CardRenderer";
import { CssVars } from "src/components/CssVars";
import { TopicHeader } from "../cards/components/TopicHeader";

export default function ViewPage() {
  return <AuthedPage orgId title="Digest" main={() => <ViewDigest />} />;
}

function ViewDigest() {
  const toaster = useToasterContext();
  const { token } = theme.useToken();
  const { setTitle } = useApplicationContext();
  const router = useRouter();
  const app = useApplicationContext();
  const orgId = app.org_id!;
  const topicId = router.query.topicId as string;
  const digests = useDeepLinks(DeepLinkType.DIGEST_2);
  const link = digests.links.data?.find((l) => l.id === topicId);

  // Store topic state in React state for local filter changes
  const [topicState, setTopicState] = useState(
    link ? { ...link.json } : undefined
  );

  const [isLoading, setIsLoading] = useState(false);

  // Handler to update filters in topic state
  const handleUpdateFilters = (newFilters: any[]) => {
    setIsLoading(true);
    setTopicState((prev: any) => ({
      ...prev,
      defaultFilters: newFilters,
    }));
  };

  // Handler to update timeframe in topic state
  const handleUpdateTimeframe = (newDateRange: any) => {
    setIsLoading(true);
    setTopicState((prev: any) => ({
      ...prev,
      defaultDateRange: newDateRange,
    }));
  };

  // Set loading to false when topicState or link changes
  useEffect(() => {
    setIsLoading(false);
  }, [topicState, link]);

  useEffect(() => {
    setTitle(link?.name || "Topic");
  }, [link?.name]);

  const menuItems = useMemo(() => {
    if (!link) return [];

    // User can edit if:
    // 1. They are the owner (link.userId === app.user.id)
    // 2. They are an org admin and it's a workspace digest (link.orgId && app.isOrgAdmin)
    // 3. They are a super admin (app.isSuperAdmin)
    const canEdit =
      link.userId === app.user?.id ||
      (link.orgId && app.isOrgAdmin) ||
      app.isSuperAdmin;

    return canEdit
      ? [
          {
            key: "edit",
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              router.push({
                pathname: "/[orgId]/topics/[topicId]/edit.v2",
                query: {
                  orgId,
                  topicId,
                },
              });
            },
          },
        ]
      : [];
  }, [
    link,
    app.user?.id,
    app.isOrgAdmin,
    app.isSuperAdmin,
    orgId,
    topicId,
    router,
  ]);

  const section = link ? toSection(link.name, link.json) : null;

  if (!link) {
    return null;
  }
  const showErrorCards =
    app.isSuperAdmin || process.env.NODE_ENV === "development";
  const canEdit =
    link.userId === app.user?.id ||
    (link.orgId && app.isOrgAdmin) ||
    app.isSuperAdmin;

  return (
    <div className="vzbl-panel-standard">
      {link.json.topicCardConfig ? (
        <div style={{ width: "100%", maxWidth: "1200px", margin: "0 auto" }}>
          <TopicHeader
            title={link.name}
            link={link}
            topic={topicState || link.json}
            canEdit={canEdit}
            onUpdateFilters={handleUpdateFilters}
            isLoading={isLoading}
            onUpdateTimeframe={handleUpdateTimeframe}
          />
          <CssVars>
            <div style={{ padding: "var(--padding-4)" }}>
              <CardRenderer
                link={
                  {
                    ...link,
                    json: topicState || link.json,
                  } as AnnotatedDeepLink<DigestV2State>
                }
                size={"expanded" as const}
                showErrorCards={showErrorCards}
              />
            </div>
          </CssVars>
        </div>
      ) : (
        <ChatAssistant
          title={link.name}
          titleExtra={
            <>
              <Tooltip title="Favorite">
                <Icon
                  component={() =>
                    digests.addBookmark.isLoading ||
                    digests.removeBookmark.isLoading ? (
                      <LoadingOutlined />
                    ) : (
                      <StarIcon filled={link.isBookmarked} />
                    )
                  }
                  disabled={!link}
                  onClick={() => {
                    if (!link) return;
                    const status = link.isBookmarked
                      ? "Removed from favorites"
                      : "Added to favorites";
                    const method = link.isBookmarked
                      ? "removeBookmark"
                      : "addBookmark";
                    digests[method]
                      .mutateAsync({ id: link.id })
                      .then(() => {
                        toaster.addMessage(ToasterMessageType.SUCCESS, {
                          message: status,
                        });
                      })
                      .catch(() => {
                        toaster.addMessage(ToasterMessageType.ERROR, {
                          message:
                            "There was an unexpected error. Please try again.",
                        });
                      });
                  }}
                />
              </Tooltip>
              {menuItems.length > 0 ? (
                <Dropdown
                  trigger={["click"]}
                  placement={"bottom"}
                  arrow
                  menu={{
                    items: menuItems,
                  }}
                >
                  <Button
                    style={{
                      paddingLeft: 8,
                      paddingRight: 8,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    type="text"
                    shape="round"
                  >
                    {digests.update.isLoading || digests.remove.isLoading ? (
                      <LoadingOutlined />
                    ) : (
                      <EllipsisOutlined
                        style={{ color: token.colorTextTertiary }}
                      />
                    )}
                  </Button>
                </Dropdown>
              ) : null}
            </>
          }
          initialPrompt={section?.prompt || ""}
          dataSources={
            section?.queries.map((query) => ({
              query,
              format: "csv" as const,
            })) || []
          }
          timeframe={{ duration: section?.queries[0]?.duration || "P7D" }}
          onClose={() => router.push(`/${orgId}/topics`)}
          contextLabel={
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <span>
                Analyzing {section?.queries.length || 0} data source
                {(section?.queries.length || 0) === 1 ? "" : "s"}
              </span>
            </div>
          }
        />
      )}
    </div>
  );
}

export const ExploreDeepLink = (props: {
  deepLink: ExploreCard;
  orgId: string;
  spanRow?: boolean;
  filters?: Condition[];
}) => {
  const { orgId } = props;
  const link = props.deepLink;
  const { token } = theme.useToken();
  const state = link?.state?.params || {};

  props.filters?.forEach((f) => {
    if (!state?.filters?.includes(f)) {
      state?.filters?.push(f);
    }
  });

  return (
    <Link
      target="_blank"
      href={{
        pathname: "/[orgId]/explore",
        query: {
          orgId,
          s: serializeParams(ensureState(state)),
          v: serializeContentView(
            getCorrectedContentView(
              link?.state?.viz,
              Boolean(link?.state?.viz?.config?.pivotId)
            )
          ),
          d: serializeDocument({
            saved: false,
            title: link.title,
          }),
          t: serializeTabs({}),
        },
      }}
      style={{ width: "100%", color: token.colorText }}
    >
      <Flex style={{ justifyContent: "space-between" }}>
        <>{link.title}</>
        <FileTextOutlined style={{ color: token.colorIcon }} />
      </Flex>
    </Link>
  );
};
